import { Add } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';

import ConfirmDeleteFieldDialog from './components/ConfirmDeleteFieldDialog';
import FieldEditorDialog from './components/FieldEditorDialog';
import GroupEditorDialog from './components/GroupEditorDialog';
import GroupSection from './components/GroupSection';
import PreviewPane from './components/PreviewPane';
import type { FieldOption } from './components/ConditionBuilder';
import type { AssignmentDraft } from './components/ProductAssignmentsEditor';
import {
    clearError,
    createField,
    createGroup,
    deleteField,
    deleteGroup,
    loadConfig,
    removeAssignment,
    reorderFields,
    reorderGroups,
    setAssignment,
    updateField,
    updateGroup,
} from './product-requirements-config.slice';
import {
    buildFieldTree,
    type CreateRequirementFieldRequest,
    type FieldAssignment,
    type ProductRequirementGroup,
    type RequirementField,
    type RequirementFieldWithChildren,
    type RequirementGroupPlacement,
    type UpdateRequirementFieldRequest,
} from './product-requirements-config.dto';

type FieldDialogState =
    | { mode: 'create'; groupId: string; parentFieldId: string | null }
    | { mode: 'edit'; groupId: string; field: RequirementField };

type GroupDialogState = { mode: 'create' } | { mode: 'edit'; group: ProductRequirementGroup };

type DeleteDialogState = { groupId: string; field: RequirementFieldWithChildren };

function assignmentsEqual(a: FieldAssignment, draft: AssignmentDraft): boolean {
    return (
        a.dataCriticality === draft.dataCriticality &&
        JSON.stringify(a.criticalityWhen ?? null) === JSON.stringify(draft.criticalityWhen ?? null)
    );
}

export default function ProductRequirementsConfigPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { canEdit, products, groups, assignments, loading, saving, error } = useAppSelector(
        (state) => state.productRequirementsConfig,
    );

    const [tab, setTab] = useState<'configuration' | 'preview'>('configuration');
    const [fieldDialog, setFieldDialog] = useState<FieldDialogState | null>(null);
    const [groupDialog, setGroupDialog] = useState<GroupDialogState | null>(null);
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);

    useEffect(() => {
        dispatch(loadConfig());
    }, [dispatch]);

    const assignmentsByField = useMemo(() => {
        const map = new Map<string, FieldAssignment[]>();
        for (const a of assignments) {
            const list = map.get(a.fieldId) ?? [];
            list.push(a);
            map.set(a.fieldId, list);
        }
        return map;
    }, [assignments]);

    // GroupSection renders SKU chips from product ids only.
    const assignedProductIdsByField = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const [fieldId, list] of assignmentsByField) {
            map.set(
                fieldId,
                list.map((a) => a.productId),
            );
        }
        return map;
    }, [assignmentsByField]);

    const dialogGroup = useMemo(() => {
        if (!fieldDialog) return null;
        return groups.find((g) => g.id === fieldDialog.groupId) ?? null;
    }, [fieldDialog, groups]);

    // Sibling codes a condition may reference: every field in the group except
    // the one being edited (the backend rejects self-references anyway).
    const fieldOptions: FieldOption[] = useMemo(() => {
        if (!dialogGroup) return [];
        const editingId = fieldDialog?.mode === 'edit' ? fieldDialog.field.id : null;
        return dialogGroup.requirementFields
            .filter((f) => f.id !== editingId)
            .map((f) => ({ code: f.code, label: f.label }));
    }, [dialogGroup, fieldDialog]);

    function handleMoveGroup(index: number, direction: -1 | 1) {
        const to = index + direction;
        if (to < 0 || to >= groups.length) return;
        const ordered = [...groups];
        [ordered[index], ordered[to]] = [ordered[to], ordered[index]];
        dispatch(reorderGroups(ordered.map((g) => g.id)));
    }

    // Sequential awaits, not parallel dispatches: every mutation thunk refetches
    // groups + assignments on completion, so concurrent ops could interleave and
    // let a stale refetch overwrite a newer one. On failure the loop stops, the
    // slice's error Alert shows the backend detail, and the dialog stays open so
    // nothing the user typed is lost.
    async function handleSubmitField(
        request: CreateRequirementFieldRequest,
        assignmentDrafts: Map<string, AssignmentDraft> | null,
    ) {
        if (!fieldDialog) return;
        try {
            if (fieldDialog.mode === 'create') {
                await dispatch(createField({ groupId: fieldDialog.groupId, req: request })).unwrap();
            } else {
                const fieldId = fieldDialog.field.id;
                // The update endpoint's DTO carries neither `code` nor `parentFieldId`
                // (both immutable) — strip them from the create-shaped draft.
                const updateRequest: Partial<CreateRequirementFieldRequest> = { ...request };
                delete updateRequest.code;
                delete updateRequest.parentFieldId;
                await dispatch(
                    updateField({
                        groupId: fieldDialog.groupId,
                        fieldId,
                        req: updateRequest as UpdateRequirementFieldRequest,
                    }),
                ).unwrap();

                // Persist the assignment delta (per-product criticality — GEN-642).
                if (assignmentDrafts) {
                    const current = new Map(
                        (assignmentsByField.get(fieldId) ?? []).map((a) => [a.productId, a] as const),
                    );
                    for (const [productId, draft] of assignmentDrafts) {
                        const existing = current.get(productId);
                        if (draft.assigned && (!existing || !assignmentsEqual(existing, draft))) {
                            await dispatch(
                                setAssignment({
                                    productId,
                                    fieldId,
                                    req: {
                                        dataCriticality: draft.dataCriticality,
                                        criticalityWhen: draft.criticalityWhen,
                                    },
                                }),
                            ).unwrap();
                        } else if (!draft.assigned && existing) {
                            await dispatch(removeAssignment({ productId, fieldId })).unwrap();
                        }
                    }
                }
            }
            setFieldDialog(null);
        } catch {
            // Error already surfaced via the slice's rejected matcher (page Alert).
        }
    }

    function handleSubmitGroup(values: { code: string; name: string; placement: RequirementGroupPlacement }) {
        if (!groupDialog) return;
        if (groupDialog.mode === 'create') {
            const displayOrder = groups.reduce((max, g) => Math.max(max, g.displayOrder + 1), 0);
            dispatch(createGroup({ code: values.code, name: values.name, displayOrder, placement: values.placement }));
        } else {
            dispatch(
                updateGroup({
                    groupId: groupDialog.group.id,
                    req: {
                        name: values.name,
                        displayOrder: groupDialog.group.displayOrder,
                        placement: values.placement,
                    },
                }),
            );
        }
        setGroupDialog(null);
    }

    function handleDeleteField(groupId: string, field: RequirementField) {
        const group = groups.find((g) => g.id === groupId);
        const node = group
            ? buildFieldTree(group.requirementFields)
                  .flatMap((root) => [root, ...root.subFields])
                  .find((f) => f.id === field.id)
            : null;
        setDeleteDialog({ groupId, field: node ?? { ...field, subFields: [] } });
    }

    function handleDeleteGroup(group: ProductRequirementGroup) {
        if (window.confirm(t('Delete group "{{name}}"?', { name: group.name }))) {
            dispatch(deleteGroup(group.id));
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                    {t('Product Requirements')}
                </Typography>
                {tab === 'configuration' && canEdit && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        disabled={loading || saving}
                        onClick={() => setGroupDialog({ mode: 'create' })}
                    >
                        {t('Add group')}
                    </Button>
                )}
            </Stack>

            <Tabs value={tab} onChange={(_, next: 'configuration' | 'preview') => setTab(next)} sx={{ mb: 2 }}>
                <Tab value="configuration" label={t('Configuration')} />
                <Tab value="preview" label={t('Preview')} />
            </Tabs>

            {tab === 'configuration' && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t(
                            'Configure the requirement groups and fields collected for each product. Changes apply immediately to the data-collection forms.',
                        )}
                    </Typography>

                    {!loading && !canEdit && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t(
                                'Read-only view — your account is not on the editor allowlist for product requirements.',
                            )}
                        </Alert>
                    )}

                    {saving && <LinearProgress sx={{ mb: 1 }} />}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            {groups.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('No groups yet. Add one to get started.')}
                                </Typography>
                            )}
                            {groups.map((group, index) => (
                                <GroupSection
                                    key={group.id}
                                    group={group}
                                    index={index}
                                    total={groups.length}
                                    products={products}
                                    assignmentsByField={assignedProductIdsByField}
                                    saving={saving}
                                    readOnly={!canEdit}
                                    onEditGroup={() => setGroupDialog({ mode: 'edit', group })}
                                    onDeleteGroup={() => handleDeleteGroup(group)}
                                    onMoveGroup={(direction) => handleMoveGroup(index, direction)}
                                    onAddField={(parentFieldId) =>
                                        setFieldDialog({ mode: 'create', groupId: group.id, parentFieldId })
                                    }
                                    onEditField={(field) => setFieldDialog({ mode: 'edit', groupId: group.id, field })}
                                    onDeleteField={(field) => handleDeleteField(group.id, field)}
                                    onReorderFields={(parentFieldId, orderedFieldIds) =>
                                        dispatch(reorderFields({ groupId: group.id, parentFieldId, orderedFieldIds }))
                                    }
                                />
                            ))}
                        </Stack>
                    )}
                </>
            )}

            {tab === 'preview' && <PreviewPane products={products} />}

            {fieldDialog && dialogGroup && (
                <FieldEditorDialog
                    key={`${fieldDialog.mode}-${fieldDialog.groupId}-${
                        fieldDialog.mode === 'edit' ? fieldDialog.field.id : (fieldDialog.parentFieldId ?? 'root')
                    }`}
                    open
                    mode={fieldDialog.mode}
                    groupPlacement={dialogGroup.placement}
                    parentFieldId={fieldDialog.mode === 'create' ? fieldDialog.parentFieldId : undefined}
                    field={fieldDialog.mode === 'edit' ? fieldDialog.field : undefined}
                    fieldOptions={fieldOptions}
                    nextDisplayOrder={dialogGroup.requirementFields.length}
                    products={products}
                    assignments={
                        fieldDialog.mode === 'edit' ? (assignmentsByField.get(fieldDialog.field.id) ?? []) : []
                    }
                    saving={saving}
                    onClose={() => setFieldDialog(null)}
                    onSubmit={handleSubmitField}
                />
            )}

            {groupDialog && (
                <GroupEditorDialog
                    key={groupDialog.mode === 'edit' ? groupDialog.group.id : 'new-group'}
                    open
                    mode={groupDialog.mode}
                    group={groupDialog.mode === 'edit' ? groupDialog.group : undefined}
                    saving={saving}
                    onClose={() => setGroupDialog(null)}
                    onSubmit={handleSubmitGroup}
                />
            )}

            {deleteDialog && (
                <ConfirmDeleteFieldDialog
                    field={deleteDialog.field}
                    assignedProducts={products.filter((p) =>
                        (assignedProductIdsByField.get(deleteDialog.field.id) ?? []).includes(p.id),
                    )}
                    saving={saving}
                    onClose={() => setDeleteDialog(null)}
                    onConfirm={() => {
                        dispatch(deleteField({ groupId: deleteDialog.groupId, fieldId: deleteDialog.field.id }));
                        setDeleteDialog(null);
                    }}
                />
            )}
        </Box>
    );
}
